import { FormattedMessage } from '@futrli/localisation';
import { UISystemIcon } from '@futrli/ui-components';
import React from 'react';

export interface Template {
  title: JSX.Element;
  link: string;
  linkName: string;
  icon?: JSX.Element;
  desc: JSX.Element;
}

export interface SectionsAndTemplates {
  sectionTitle: JSX.Element;
  sectionIcon: JSX.Element;
  templates: Template[];
}

// list of sections and templates that a user can add to their import sheet
export const sectionsAndTemplates: SectionsAndTemplates[] = [
  {
    sectionTitle: <FormattedMessage id="reporting.people" defaultMessage="People" />,
    sectionIcon: <UISystemIcon iconName="people" />,
    templates: [
      {
        title: <FormattedMessage id="import.templates-payroll-uk.title" defaultMessage="UK payroll" />,
        link: 'https://docs.google.com/spreadsheets/d/1DXrtTyJy7KVFeGiYtM8EQI0OhDGxECnhTwUwjN6dxc4/edit?usp=sharing',
        linkName: 'UK payroll',
        icon: <UISystemIcon iconName="uk-flag" width="40" height="40" />,
        desc: (
          <FormattedMessage
            id="import.templates-payroll-uk.desc"
            defaultMessage="Accurately calculate your staff costs, including PAYE, pensions, National Insurance, bonuses etc."
          />
        )
      },
      {
        title: <FormattedMessage id="import.templates-payroll-au.title" defaultMessage="Australian payroll" />,
        link: 'https://docs.google.com/spreadsheets/d/1DXrtTyJy7KVFeGiYtM8EQI0OhDGxECnhTwUwjN6dxc4/edit?usp=sharing',
        linkName: 'Australian payroll',
        icon: <UISystemIcon iconName="au-flag" width="40" height="40" />,
        desc: (
          <FormattedMessage
            id="import.templates-payroll-au.desc"
            defaultMessage="Accurately calculate your staff costs forecasting, including PAYG, Medicare, Superannuation, commission etc."
          />
        )
      },
      {
        title: <FormattedMessage id="import.templates_payroll_nz.title" defaultMessage="NZ Payroll" />,
        link: 'https://docs.google.com/spreadsheets/d/1DXrtTyJy7KVFeGiYtM8EQI0OhDGxECnhTwUwjN6dxc4/edit#gid=1143236173',
        linkName: 'NZ Payroll',
        icon: <UISystemIcon iconName="nz-flag" width="40" height="40" />,
        desc: (
          <FormattedMessage
            id="import.templates_payroll_nz.desc"
            defaultMessage="Accurately calculate your staff costs forecasting, including sales tax, Kiwisaver, commission etc."
          />
        )
      }
    ]
  },
  {
    sectionTitle: <FormattedMessage id="import.templates-revenue" defaultMessage="Revenue / funding / payments" />,
    sectionIcon: <UISystemIcon iconName="heartbeat" />,
    templates: [
      {
        title: <FormattedMessage id="import.templates-recurring-revenue.title" defaultMessage="Recurring Revenue" />,
        link: 'https://docs.google.com/spreadsheets/d/1WWfgHqic84OVzYMKECcL0FTuXy03qe7Xd36BDyI6jPs/edit?usp=sharing',
        linkName: 'Recurring Revenue',
        desc: (
          <FormattedMessage
            id="import.templates-recurring-revenue.desc"
            defaultMessage="Predict up to 10 streams of recurring revenue based on growth and churn assumptions."
          />
        )
      },
      {
        title: <FormattedMessage id="import.templates-loan-amortization.title" defaultMessage="Loan Amortization" />,
        link: 'https://docs.google.com/spreadsheets/d/1i7eYUAb3xZVVCz350XYn-uhMylJJwh17UzSiwQAcOEA/edit?usp=sharing',
        linkName: 'Loan Amortization',
        desc: (
          <FormattedMessage
            id="import.templates-loan-amortization.desc"
            defaultMessage="If you are repaying loans or looking to take one out, use this to calculate repayments and interest."
          />
        )
      },
      {
        title: <FormattedMessage id="import.templates-client-tracker.title" defaultMessage="Client Tracker" />,
        link: 'https://docs.google.com/spreadsheets/d/1m6dzYBzqTghb4G0RF2LkhzLXKaz0gAJaJglOeP--AfI/edit?usp=sharing',
        linkName: 'Client Tracker',
        desc: (
          <FormattedMessage
            id="import.templates-client-tracker.desc"
            defaultMessage="Predict a combination of one off and monthly recurring revenue for up to 50 clients."
          />
        )
      },
      {
        title: <FormattedMessage id="import.templates-group-payments.title" defaultMessage="Grouped payments" />,
        link: 'https://docs.google.com/spreadsheets/d/1jf1yiBf_OS8P0LfWCVFxwpl1TZ7mSGQ5MGRrp4acsds/edit?usp=sharing',
        linkName: 'Grouped payments',
        desc: (
          <FormattedMessage
            id="import.templates-group-payments.desc"
            defaultMessage="Tell us about your accruals, prepayments or deferred revenue and we will handle the journals."
          />
        )
      },
      {
        title: (
          <FormattedMessage id="import.templates-invoice-discounting.title" defaultMessage="Invoice Discounting" />
        ),
        link: 'https://docs.google.com/spreadsheets/d/1cGdzx9Ju8sMIpY_IHLqOLQxKfCn5LVgWZLSsMu1UyGI/edit?usp=sharing',
        linkName: 'Invoice Discounting',
        desc: (
          <FormattedMessage
            id="import.templates-invoice-discounting.desc"
            defaultMessage="If you are receiving payment via invoice discounting, use this to accurately predict costs and cash flows."
          />
        )
      }
    ]
  },
  {
    sectionTitle: <FormattedMessage id="import.templates-non-financial" defaultMessage="Non-financial / custom" />,
    sectionIcon: <UISystemIcon iconName="zap" />,
    templates: [
      {
        title: <FormattedMessage id="import.templates-custom-monthly.title" defaultMessage="Custom monthly" />,
        link: 'https://docs.google.com/spreadsheets/d/1xVJoe7Z6KhchqyHl6DEBPaCAjp-hVHJsqLpNgIua0M8/edit?usp=sharing',
        linkName: 'Custom monthly',
        desc: (
          <FormattedMessage
            id="import.templates-custom-monthly.desc"
            defaultMessage="A blank template that lets you pull any monthly value directly into your cashflow."
          />
        )
      },
      {
        title: <FormattedMessage id="import.templates-custom-daily.title" defaultMessage="Custom daily" />,
        link: 'https://docs.google.com/spreadsheets/d/1xVJoe7Z6KhchqyHl6DEBPaCAjp-hVHJsqLpNgIua0M8/edit?usp=sharing',
        linkName: 'Custom daily',
        desc: (
          <FormattedMessage
            id="import.templates-custom-daily.desc"
            defaultMessage="A completely flexible template allowing daily values to be pulled directly into Predict."
          />
        )
      },
      {
        title: <FormattedMessage id="import.templates_stock.title" defaultMessage="Stock" />,
        link: 'https://docs.google.com/spreadsheets/d/1mUViSkY6Co_EomylKRhTqErnNWyO0H_TSuFIv8wAarg/edit#gid=1982044930',
        linkName: 'Stock',
        desc: (
          <FormattedMessage
            id="import.templates_stock.desc"
            defaultMessage="Predict cost of sales and related stock movements."
          />
        )
      }
    ]
  }
];
